import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MagnifyingGlass, GraduationCap, LinkedinLogo, HandHeart, NotePencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

function ProfileDialog({ alumnus, onClose, onSaved, canEdit }) {
  const [form, setForm] = useState({
    graduation_year: "", current_company: "", current_role: "",
    current_city: "", linkedin_url: "", bio: "", willing_to_mentor: false,
    contact_email: "", contact_phone: "",
    is_public_in_alumni_directory: false,
  });
  useEffect(() => {
    if (!alumnus) return;
    setForm({
      graduation_year: alumnus.graduation_year || "",
      current_company: alumnus.current_company || "",
      current_role: alumnus.current_role || "",
      current_city: alumnus.current_city || "",
      linkedin_url: alumnus.linkedin_url || "",
      bio: alumnus.bio || "",
      willing_to_mentor: !!alumnus.willing_to_mentor,
      contact_email: alumnus.contact_email || "",
      contact_phone: alumnus.contact_phone || "",
      is_public_in_alumni_directory: !!alumnus.is_public_in_alumni_directory,
    });
  }, [alumnus]);
  if (!alumnus) return null;
  const save = async () => {
    const payload = { ...form };
    if (payload.graduation_year) payload.graduation_year = Number(payload.graduation_year);
    else delete payload.graduation_year;
    try {
      await api.patch(`/alumni/${alumnus.id}/profile`, payload);
      toast.success("Profile updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="alumni-profile-dialog">
      <div className="bg-white rounded-lg p-5 max-w-lg w-full space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-medium">{alumnus.name}</div>
          <Badge variant="outline" className="font-mono text-[10px]">{alumnus.vs_id}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Graduation year</Label><Input data-testid="alpr-year" disabled={!canEdit} type="number" min="2000" max="2099" value={form.graduation_year} onChange={(e) => setForm({ ...form, graduation_year: e.target.value })} className="bg-white font-mono" /></div>
          <div><Label>Current city</Label><Input data-testid="alpr-city" disabled={!canEdit} value={form.current_city} onChange={(e) => setForm({ ...form, current_city: e.target.value })} className="bg-white" /></div>
          <div><Label>Current company</Label><Input data-testid="alpr-company" disabled={!canEdit} value={form.current_company} onChange={(e) => setForm({ ...form, current_company: e.target.value })} className="bg-white" /></div>
          <div><Label>Current role</Label><Input data-testid="alpr-role" disabled={!canEdit} value={form.current_role} onChange={(e) => setForm({ ...form, current_role: e.target.value })} className="bg-white" /></div>
          <div className="col-span-2"><Label>LinkedIn URL</Label><Input data-testid="alpr-linkedin" disabled={!canEdit} value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/…" className="bg-white" /></div>
          <div><Label>Contact email</Label><Input data-testid="alpr-email" disabled={!canEdit} type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-white" /></div>
          <div><Label>Contact phone</Label><Input data-testid="alpr-phone" disabled={!canEdit} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="bg-white font-mono" /></div>
          <div className="col-span-2"><Label>Bio</Label><Textarea data-testid="alpr-bio" disabled={!canEdit} rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="bg-white" /></div>
        </div>
        <div className="flex items-center gap-3"><Switch checked={form.willing_to_mentor} onCheckedChange={(v) => setForm({ ...form, willing_to_mentor: v })} disabled={!canEdit} data-testid="alpr-mentor" /><span className="text-sm">Willing to mentor current students</span></div>
        <div className="flex items-center gap-3 p-2 rounded bg-amber-50 border border-amber-200/60">
          <Switch checked={form.is_public_in_alumni_directory} onCheckedChange={(v) => setForm({ ...form, is_public_in_alumni_directory: v })} disabled={!canEdit} data-testid="alpr-public" />
          <div className="text-sm">
            <div className="font-medium">Show in public alumni directory</div>
            <div className="text-[10px] text-[var(--vs-text-secondary)]">Card will appear on the no-auth <code>/alumni-directory</code> page. Only non-PII fields are shown.</div>
          </div>
        </div>
        <div className="flex justify-between items-center pt-2">
          <Link to="/app/donations" className="text-[var(--vs-secondary)] underline decoration-dotted text-sm inline-flex items-center gap-1.5" data-testid="alpr-donate">
            <HandHeart size={14} /> Donate now (80G)
          </Link>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            {canEdit && <Button data-testid="alpr-save" onClick={save}>Save</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function DirectoryTab({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => api.get("/alumni").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const visible = rows.filter((s) => !filter || (s.name?.toLowerCase().includes(filter.toLowerCase()) || s.vs_id?.toLowerCase().includes(filter.toLowerCase())));
  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative max-w-xs w-full">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vs-text-secondary)]" />
          <Input data-testid="alumni-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search name, VS-ID" className="pl-9 bg-white" />
        </div>
        <div className="text-xs font-mono text-[var(--vs-text-secondary)]">{visible.length} alumni</div>
      </div>
      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">VS-ID</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Year</th>
              <th className="px-4 py-3">Company · Role</th>
              <th className="px-4 py-3">City</th>
              <th className="px-4 py-3">Mentor?</th>
              <th className="px-4 py-3">Links</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => (
              <tr key={s.id} data-testid={`alumni-row-${s.vs_id}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono text-xs">{s.vs_id}</td>
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3 font-mono">{s.graduation_year || "—"}</td>
                <td className="px-4 py-3 text-xs">{s.current_company ? <span>{s.current_company}{s.current_role ? ` · ${s.current_role}` : ""}</span> : "—"}</td>
                <td className="px-4 py-3 text-xs">{s.current_city || "—"}</td>
                <td className="px-4 py-3">{s.willing_to_mentor && <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]">YES</Badge>}</td>
                <td className="px-4 py-3">{s.linkedin_url && <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[var(--vs-secondary)]" data-testid={`alumni-linkedin-${s.vs_id}`}><LinkedinLogo size={16} /></a>}</td>
                <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => setOpen(s)} data-testid={`alumni-open-${s.vs_id}`}><NotePencil size={12} className="mr-1" /> {canEdit ? "Edit" : "View"}</Button></td>
              </tr>
            ))}
            {!visible.length && (<tr><td colSpan={8} className="px-4 py-12 text-center text-[var(--vs-text-secondary)]"><GraduationCap size={28} weight="thin" className="mx-auto mb-2 opacity-50" />No alumni yet.</td></tr>)}
          </tbody>
        </table>
      </div>
      <ProfileDialog alumnus={open} canEdit={canEdit} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />
    </>
  );
}

function ByYearTab() {
  const [groups, setGroups] = useState([]);
  useEffect(() => { api.get("/alumni/by-year").then((r) => setGroups(r.data.by_year || [])); }, []);
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.year} className="vs-card p-4" data-testid={`alumni-year-${g.year}`}>
          <div className="font-medium mb-2"><GraduationCap size={16} className="inline mr-1.5 opacity-70" />{g.year || "Year unknown"} · <span className="text-[var(--vs-text-secondary)] text-xs">{g.members.length} member{g.members.length === 1 ? "" : "s"}</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {g.members.map((m) => (
              <div key={m.id} className="p-2 rounded border border-[var(--vs-border)]/40 bg-[var(--vs-bg-subtle)] text-xs">
                <div className="font-medium">{m.name}</div>
                <div className="text-[10px] text-[var(--vs-text-secondary)]">{m.current_company || "—"} {m.current_city && `· ${m.current_city}`}</div>
                {m.willing_to_mentor && <Badge variant="outline" className="mt-1 border-emerald-300 text-emerald-700 bg-emerald-50 text-[9px]">Mentor</Badge>}
              </div>
            ))}
          </div>
        </div>
      ))}
      {!groups.length && <div className="vs-card p-8 text-center text-sm text-[var(--vs-text-secondary)]">No alumni yet.</div>}
    </div>
  );
}

export default function Alumni() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "school_admin", "hostel_admin"].includes(user?.role);
  return (
    <DashboardLayout title="Alumni Register" subtitle="Profiles + class-wise directory. Click 'Donate now' to contribute back via the 80G receipt pipeline.">
      <Tabs defaultValue="directory">
        <TabsList>
          <TabsTrigger value="directory" data-testid="tab-alumni-directory">Directory</TabsTrigger>
          <TabsTrigger value="by-year" data-testid="tab-alumni-year">By year</TabsTrigger>
        </TabsList>
        <TabsContent value="directory"><DirectoryTab canEdit={canEdit} /></TabsContent>
        <TabsContent value="by-year"><ByYearTab /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
